import {Component, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import * as fromUser from 'src/app/_selectors/user.selectors';
import {UntypedFormControl} from '@angular/forms';
import {JournalDefaultSettings} from '../journal-settings/journal-settings.defaults';
import {NgbDateParserFormatter, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {JournalSettingsKeys} from '../journal-settings/journal-settings.defaults2';
import {SettingsManageService} from '../../_services/settings-manage.service';
import {NgbDateCustomParserFormatter} from 'shared/_services/ngb-date-adapter.service';
import {
	CLICK_OUTSIDE_TRIGGER,
	CONTENT_LINES_NUMBER,
	ENTRIES_PER_PAGE,
	JOURNAL_SETTINGS_KEYS_PREFIX,
	OVERVIEW_ENTRIES_LIMIT
} from 'shared/magic';
import * as journalActions from '../../../../journal/src/_actions/journal.actions';
import * as fromJournal from '../../../../journal/src/_selectors/journal.selectors';
import {selectForManage} from '../../../../journal/src/_reducers/entry-type-entity.reducer';
import {filter, map} from 'rxjs/operators';
import {Subscription} from 'rxjs';

const TIMER_DETAIL_KEYS = [
	JournalSettingsKeys.showTimerPatientName,
	JournalSettingsKeys.showTimerCpr,
	JournalSettingsKeys.showTimerConsultationType
];

@Component({
	selector: 'lib-settings-bar',
	templateUrl: './settings-bar.component.html',
	styleUrls: ['./settings-bar.component.scss'],
	providers: [{provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter}]
})
export class SettingsBarComponent implements OnInit, OnDestroy {
	private timerSub: Subscription;
	private timerResetSub: Subscription;

	loggedUser$ = this.store.select(fromUser.selectLoggedUser);
	formRef: Map<string, UntypedFormControl> = new Map();
	templates$ = this.manageSettings.templates$;
	numberOfLines$ = this.store.select(fromJournal.selectJournalSettingByKey(JournalSettingsKeys.foldContentLinesNumber));
	entryTypes$ = this.store.select(selectForManage).pipe(
		filter((f) => !!f),
		map((entryTypes) => entryTypes.map((e, index) =>
			({type: e.entryType, slug: e.entryTypeSlug, index})))
	);
	contentLinesNum = CONTENT_LINES_NUMBER;
	defaults = JournalDefaultSettings;
	fromDate: NgbDateStruct;
	keys = JournalSettingsKeys;
	entriesPerPage = ENTRIES_PER_PAGE;
	overviewEntriesLimit = OVERVIEW_ENTRIES_LIMIT;
	entryTypeControl = new UntypedFormControl();
	clickOutsideTrigger = CLICK_OUTSIDE_TRIGGER;
	timerEnabled$ = this.store.select(fromJournal.selectJournalSettingByKey(JournalSettingsKeys.showConsultationTimer)).pipe(
		map(s => s == null ? true : Boolean(s.value))
	);

	constructor(
		private store: Store,
		private manageSettings: SettingsManageService,
	) {}

	private resetTimerDetail(key: string): void {
		const control = this.formRef.get(key);
		if (control.value === false) return;
		control.setValue(false, {emitEvent: false});
		this.store.dispatch(journalActions.saveSingleKey({key: `${JOURNAL_SETTINGS_KEYS_PREFIX}.${key}`, value: false}));
	}

	private wireTimerControls(): void {
		this.timerSub = this.timerEnabled$.subscribe(enabled => {
			TIMER_DETAIL_KEYS.forEach(k => {
				const control = this.formRef.get(k);
				if (enabled) {
					control.enable({emitEvent: false});
				} else {
					control.disable({emitEvent: false});
				}
			});
		});
		this.timerResetSub = this.formRef.get(JournalSettingsKeys.showConsultationTimer).valueChanges.pipe(
			filter(enabled => !enabled)
		).subscribe(() => TIMER_DETAIL_KEYS.forEach(k => this.resetTimerDetail(k)));
	}

	ngOnInit(): void {
		this.createForm();
		this.wireTimerControls();
	}

	ngOnDestroy(): void {
		this.timerSub?.unsubscribe();
		this.timerResetSub?.unsubscribe();
	}

	createForm() {
		Object.keys(JournalSettingsKeys).forEach((key: string) => {
			this.manageSettings.bindSettingProperty(JOURNAL_SETTINGS_KEYS_PREFIX, key, this.formRef);
		});
		this.setEntryTypesExtended();
	}

	setEntryTypesExtended() {
		this.entryTypes$.subscribe((entryTypes) => {
			const { value } = this.formRef.get('entryTypeExtended');
			if (!value) return;
			const selected =  entryTypes.filter((eType) => value.includes(eType.slug));
			this.entryTypeControl.patchValue(selected, {emitEvent: false});
		});
		this.entryTypeControl.valueChanges.subscribe(((v: string[]) => {
			this.formRef.get('entryTypeExtended').patchValue(JSON.stringify(v));
		}));
	}

	compareWithIndex(...args: {type: string, slug: string, index: number}[]): boolean {
		const [a, b] = args;
		return a.type === b.type && a.index === b.index;
	}
}
